import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import WppChat from "../components/whatsappchat"
import axios from 'axios';
import { 
  TrashIcon, 
  PlusIcon, 
  MinusIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  TruckIcon,
  ShieldCheckIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

// Paleta de colores elegante y profesional
const colors = {
  primary: '#0C4B45',
  primaryDark: '#083D38',
  primaryLight: '#83F4E9',
  secondary: '#662D8F',
  secondaryLight: '#F2A9FD',
  accent: '#4CAF50',
  accentDark: '#2E7D32',
  backgroundLight: '#F8FCFA',
  backgroundLighter: '#EFF8F5',
  textDark: '#0C4B45',
  textMedium: '#4A6B57',
  textLight: '#B5EAD7',
  error: '#EF5350',
  success: '#4CAF50',
  border: '#E0E7ED',
  cardHover: '#F5FBF8',
};

// Constantes para configuración EN PRODUCCIÓN
const API_URL = process.env.REACT_APP_API_BASE_URL || '';
const SHIPPING_DRAFT_KEY = 'pcvasquezCheckoutShippingDraft';
const PENDING_CHECKOUT_KEY = 'pcvasquezPendingCheckout';
const PRODUCT_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160"%3E%3Crect width="160" height="160" fill="%23EFF8F5"/%3E%3Cpath d="M45 52h70l-6 62H51l-6-62Z" fill="none" stroke="%230C4B45" stroke-width="6" stroke-linejoin="round"/%3E%3Cpath d="M62 52a18 18 0 0 1 36 0" fill="none" stroke="%230C4B45" stroke-width="6" stroke-linecap="round"/%3E%3C/svg%3E';

// Las credenciales de Wompi se mantienen exclusivamente en el servidor.

// BLOQUEO SUPER AGRESIVO DEL SDK WOMPI
(function() {
  if (typeof window === 'undefined') return;
  
  console.log('🔧 Iniciando bloqueo agresivo del SDK Wompi...');
  
  const removeWompiScripts = () => {
    const scripts = document.querySelectorAll('script');
    scripts.forEach(script => {
      if (script.src && (
        script.src.includes('wompi') || 
        script.src.includes('checkout.wompi.co') ||
        script.src.includes('api.wompi.co') ||
        script.src.includes('widget.js') ||
        script.src.includes('v1.js') ||
        script.src.includes('v2.js')
      )) {
        console.log('🗑️ Eliminando script de Wompi:', script.src);
        script.remove();
      }
    });
  };
  
  const blockWompiGlobals = () => {
    const globalProps = ['$wompi', 'Wompi', 'wompi', '__WOMPI__', 'wompiCheckout'];
    
    globalProps.forEach(prop => {
      Object.defineProperty(window, prop, {
        value: null,
        writable: false,
        configurable: false,
        enumerable: false
      });
    });
  };
  
  const blockDynamicLoading = () => {
    const originalAppendChild = Element.prototype.appendChild;
    const originalCreateElement = Document.prototype.createElement;
    
    Element.prototype.appendChild = function(node) {
      if (node.src && (
        node.src.includes('wompi') || 
        node.src.includes('checkout.wompi.co') ||
        node.src.includes('api.wompi.co') ||
        node.src.includes('widget.js') ||
        node.src.includes('v1.js') ||
        node.src.includes('v2.js')
      )) {
        console.log('🚫 Bloqueado appendChild de elemento Wompi:', node.src);
        return node;
      }
      return originalAppendChild.call(this, node);
    };
    
    Document.prototype.createElement = function(tagName, options) {
      const element = originalCreateElement.call(this, tagName, options);
      
      if (tagName.toLowerCase() === 'script') {
        const originalSetAttribute = element.setAttribute;
        element.setAttribute = function(name, value) {
          if (name === 'src' && value && (
            value.includes('wompi') || 
            value.includes('checkout.wompi.co') ||
            value.includes('api.wompi.co') ||
            value.includes('widget.js') ||
            value.includes('v1.js') ||
            value.includes('v2.js')
          )) {
            console.log('🚫 Bloqueado setAttribute de script Wompi:', value);
            return;
          }
          return originalSetAttribute.call(this, name, value);
        };
      }
      
      return element;
    };
  };
  
  removeWompiScripts();
  blockWompiGlobals();
  blockDynamicLoading();
  
  setInterval(removeWompiScripts, 1000);
  
  console.log('✅ Bloqueo completo del SDK Wompi implementado');
})();

// Componente para mostrar mensajes de estado
const StatusMessage = ({ type, message, onRetry, onDismiss }) => {
  const icons = {
    error: ExclamationTriangleIcon,
    success: CheckCircleIcon,
    info: InformationCircleIcon
  };
  
  const iconColors = {
    error: 'text-red-500',
    success: 'text-green-500',
    info: 'text-blue-500'
  };
  
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };
  
  const IconComponent = icons[type];
  
  return (
    <div className={`border rounded-xl p-4 mb-6 flex items-start shadow-sm ${styles[type]}`} role="alert">
      <IconComponent className={`h-5 w-5 mr-3 mt-0.5 flex-shrink-0 ${iconColors[type]}`} />
      <div className="flex-1">
        <p className="font-medium">{message}</p>
        {type === 'error' && (
          <div className="mt-2 flex space-x-3">
            <button 
              onClick={onRetry}
              className="text-sm font-medium underline hover:no-underline"
            >
              Recargar página
            </button>
            <button 
              onClick={onDismiss}
              className="text-sm font-medium underline hover:no-underline"
            >
              Intentar nuevamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para el loader
const Loader = () => (
  <div className="flex justify-center items-center h-screen">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-primary rounded-full animate-spin"></div>
    </div>
  </div>
);

// Componente para elementos del carrito
const CartItem = ({ item, onUpdateQuantity, onRemoveItem, getImageUrl }) => {
  const [imageError, setImageError] = useState(false);
  
  const handleQuantityChange = useCallback((newQuantity) => {
    onUpdateQuantity(item._id, newQuantity);
  }, [item._id, onUpdateQuantity]);

  return (
    <li className="p-5 flex flex-col gap-4 border-b border-gray-100 last:border-b-0 hover:bg-[#F5FBF8] transition-colors duration-150 sm:flex-row sm:items-center">
      <div className="flex-shrink-0">
        <img
          src={imageError ? PRODUCT_PLACEHOLDER : getImageUrl(item.image)}
          alt={item.name}
          className="h-24 w-24 object-cover rounded-xl border border-gray-100 shadow-sm"
          onError={() => setImageError(true)}
          loading="lazy"
        />
      </div>
      <div className="flex-1 flex flex-col justify-between min-w-0">
        <div className="flex justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{item.name}</h3>
            <p className="text-gray-600 text-sm">${item.price.toLocaleString()} c/u</p>
          </div>
          <p className="text-lg font-bold text-[#0C4B45] whitespace-nowrap">
            ${(item.price * item.quantity).toLocaleString()}
          </p>
        </div>
        
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center border border-gray-200 rounded-lg bg-white shadow-sm" aria-label={`Cantidad de ${item.name}`}>
            <button
              onClick={() => handleQuantityChange(item.quantity - 1)}
              disabled={item.quantity <= 1}
              className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Disminuir cantidad"
            >
              <MinusIcon className="h-4 w-4" />
            </button>
            <span className="px-3 py-1 text-gray-700 text-sm font-medium min-w-[2rem] text-center">
              {item.quantity}
            </span>
            <button 
              onClick={() => handleQuantityChange(item.quantity + 1)}
              disabled={Number.isFinite(Number(item.stock)) && item.quantity >= Number(item.stock)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Aumentar cantidad"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>
          
          <button
            onClick={() => onRemoveItem(item._id)}
            className="text-red-500 hover:text-red-700 p-2 transition-colors rounded-lg hover:bg-red-50"
            aria-label="Eliminar producto"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </li>
  );
};

// Componente para el formulario de envío - MODIFICADO PARA PRECARGAR DATOS DEL USUARIO
const ShippingForm = ({ shippingInfo, onChange, errors = {}, user = null }) => {
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    onChange(name, value);
  }, [onChange]);

  // Prellenar con datos del usuario si está disponible y los campos están vacíos
  useEffect(() => {
    if (user) {
      const updates = {};
      
      // Solo prellenar si el campo está vacío
      if (!shippingInfo.name && user.name) updates.name = user.name;
      if (!shippingInfo.email && user.email) updates.email = user.email;
      if (!shippingInfo.phone && user.phone) updates.phone = user.phone;
      // La dirección siempre la confirma el cliente en cada compra.
      if (!shippingInfo.city && user.city) updates.city = user.city;
      if (!shippingInfo.state && user.state) updates.state = user.state;
      if (!shippingInfo.postalCode && user.postalCode) updates.postalCode = user.postalCode;
      if (!shippingInfo.legalId && user.legalId) updates.legalId = user.legalId;
      if (!shippingInfo.legalIdType && user.legalIdType) updates.legalIdType = user.legalIdType;
      
      // Aplicar las actualizaciones si hay alguna
      if (Object.keys(updates).length > 0) {
        Object.keys(updates).forEach(key => {
          onChange(key, updates[key]);
        });
      }
    }
  }, [user, shippingInfo, onChange]);

  return (
    <div className="space-y-5 rounded-xl bg-[#F8FCFA] p-4 border border-[#DCECE7]">
      <div className="flex items-center gap-2">
        <TruckIcon className="h-5 w-5 text-[#0C4B45]" />
        <h3 className="font-semibold text-gray-900">Información de Envío</h3>
        {user && (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
            Datos personales precargados
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Nombre Completo *
          </label>
          <input
            type="text"
            name="name"
            value={shippingInfo.name}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={user?.name ? `Ej: ${user.name}` : "Ingresa tu nombre completo"}
            required
          />
          {errors.name && <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Correo Electrónico *
          </label>
          <input
            type="email"
            name="email"
            value={shippingInfo.email}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={user?.email ? `Ej: ${user.email}` : "tu@email.com"}
            required
          />
          {errors.email && <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>}
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Dirección de entrega *
          </label>
          <input
            type="text"
            name="address"
            value={shippingInfo.address}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.address ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Calle 123 # 45-67, apartamento 301"
            required
          />
          <p className="text-xs text-gray-500 mt-1.5">Escribe la dirección exacta donde deseas recibir este pedido.</p>
          {errors.address && <p className="text-red-500 text-xs mt-1.5">{errors.address}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Ciudad *
          </label>
          <input
            type="text"
            name="city"
            value={shippingInfo.city}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.city ? 'border-red-500' : 'border-gray-300'
            }`}
            required
            placeholder={user?.city ? `Ej: ${user.city}` : "Bogotá"}
          />
          {errors.city && <p className="text-red-500 text-xs mt-1.5">{errors.city}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Departamento/Estado *
          </label>
          <input
            type="text"
            name="state"
            value={shippingInfo.state}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.state ? 'border-red-500' : 'border-gray-300'
            }`}
            required
            placeholder={user?.state ? `Ej: ${user.state}` : "Cundinamarca"}
          />
          {errors.state && <p className="text-red-500 text-xs mt-1.5">{errors.state}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Teléfono *
          </label>
          <input
            type="tel"
            name="phone"
            value={shippingInfo.phone}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            required
            placeholder={user?.phone ? `Ej: ${user.phone}` : "3233019836"}
          />
          <p className="text-xs text-gray-500 mt-1.5">Sin código de país (57)</p>
          {errors.phone && <p className="text-red-500 text-xs mt-1.5">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Código Postal
          </label>
          <input
            type="text"
            name="postalCode"
            value={shippingInfo.postalCode}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition"
            placeholder={user?.postalCode ? `Ej: ${user.postalCode}` : "110111"}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Tipo de Documento *
          </label>
          <select
            name="legalIdType"
            value={shippingInfo.legalIdType}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition"
            required
          >
            <option value="CC">Cédula</option>
            <option value="CE">Cédula Extranjería</option>
            <option value="TI">Tarjeta Identidad</option>
            <option value="PP">Pasaporte</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Documento de Identidad *
          </label>
          <input
            type="text"
            name="legalId"
            value={shippingInfo.legalId}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition ${
              errors.legalId ? 'border-red-500' : 'border-gray-300'
            }`}
            required
            placeholder={user?.legalId ? `Ej: ${user.legalId}` : "1234567890"}
          />
          {errors.legalId && <p className="text-red-500 text-xs mt-1.5">{errors.legalId}</p>}
        </div>
      </div>
    </div>
  );
};

const CartPage = () => {
  const { 
    cartItems = [], 
    removeFromCart, 
    updateQuantity,
    subtotal = 0,
    clearCart
  } = useCart();
  
  const { loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  
  const [processing, setProcessing] = useState(false);
  const [cleaningPendingCheckout, setCleaningPendingCheckout] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // Estado inicial del formulario - ahora incluye precarga de datos del usuario
  const getInitialShippingInfo = () => {
    const baseInfo = {
      name: '',
      email: '',
      address: '',
      city: '',
      state: '',
      phone: '',
      postalCode: '',
      legalId: '',
      legalIdType: 'CC'
    };

    let savedDraft = {};
    try {
      savedDraft = JSON.parse(localStorage.getItem(SHIPPING_DRAFT_KEY) || '{}');
    } catch (_) {
      localStorage.removeItem(SHIPPING_DRAFT_KEY);
    }

    // Si hay usuario autenticado, precargar sus datos sin sobrescribir lo que ya escribió.
    if (user) {
      return {
        ...baseInfo,
        ...savedDraft,
        name: savedDraft.name || user.name || '',
        email: savedDraft.email || user.email || '',
        phone: savedDraft.phone || user.phone || '',
        // No reutilizamos direcciones del perfil: cada pedido debe indicar su entrega.
        address: savedDraft.address || '',
        city: savedDraft.city || user.city || '',
        state: savedDraft.state || user.state || '',
        postalCode: savedDraft.postalCode || user.postalCode || '',
        legalId: savedDraft.legalId || user.legalId || '',
        legalIdType: savedDraft.legalIdType || user.legalIdType || 'CC'
      };
    }

    return { ...baseInfo, ...savedDraft };
  };

  const [shippingInfo, setShippingInfo] = useState(getInitialShippingInfo);

  // Actualizar formulario cuando el usuario cambie (inicia sesión)
  useEffect(() => {
    setShippingInfo(previous => {
      const initialInfo = getInitialShippingInfo();
      return Object.keys(initialInfo).reduce((merged, key) => {
        merged[key] = previous[key] || initialInfo[key];
        return merged;
      }, {});
    });
  }, [user]);

  // Mantiene la información del formulario al volver desde el checkout externo.
  useEffect(() => {
    localStorage.setItem(SHIPPING_DRAFT_KEY, JSON.stringify(shippingInfo));
  }, [shippingInfo]);

  // Volver con el botón del navegador desde Wompi no pasa por la página de resultado.
  // En ese caso anulamos de forma segura el borrador anterior antes de crear otro.
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem('token');
    // El perfil puede tardar en hidratarse al restaurar una pestaña, pero el token
    // ya permite limpiar el checkout anterior de inmediato.
    if (authLoading || !token) return undefined;
    let pendingCheckout = null;
    try {
      pendingCheckout = JSON.parse(localStorage.getItem(PENDING_CHECKOUT_KEY) || 'null');
    } catch (_) {
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
    }
    if (!pendingCheckout?.orderId) return undefined;

    let cleanupStarted = false;
    const cancelDraft = async () => {
      if (cleanupStarted || !localStorage.getItem(PENDING_CHECKOUT_KEY)) return;
      cleanupStarted = true;
      setCleaningPendingCheckout(true);
      try {
        await axios.post(`${API_URL}/api/orders/${pendingCheckout.orderId}/cancel-unpaid`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        localStorage.removeItem(PENDING_CHECKOUT_KEY);
      } catch (cleanupError) {
        // Un 409 significa que Wompi ya tiene una transacción; se bloquea un pago duplicado.
        if (mounted) {
          setError(cleanupError.response?.status === 409
            ? 'Tu pago anterior todavía está siendo verificado. No inicies otro pago hasta que se actualice el estado.'
            : 'No pudimos verificar el intento de pago anterior. Actualiza la página antes de volver a intentarlo.');
        }
      } finally {
        cleanupStarted = false;
        if (mounted) setCleaningPendingCheckout(false);
      }
    };

    cancelDraft();
    const handlePageShow = (event) => {
      // Cuando el navegador restaura el carrito desde su caché al volver de Wompi.
      if (event.persisted) cancelDraft();
    };
    const handleVisibilityChange = () => {
      // Algunos navegadores no emiten pageshow al volver desde un checkout externo.
      if (document.visibilityState === 'visible') cancelDraft();
    };
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      mounted = false;
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authLoading]);

  useEffect(() => {
    console.log('🔍 Verificando bloqueo de Wompi SDK...');
    
    const checkForWompiScripts = () => {
      const scripts = document.querySelectorAll('script');
      scripts.forEach(script => {
        if (script.src && script.src.includes('wompi')) {
          console.log('⚠️ Script de Wompi detectado, eliminando:', script.src);
          script.remove();
        }
      });
    };
    
    const interval = setInterval(checkForWompiScripts, 500);
    return () => clearInterval(interval);
  }, []);

  // CORRECCIÓN PRINCIPAL: Función mejorada para obtener URLs de imágenes
  const getImageUrl = (imageName) => {
    if (!imageName) return '/placeholder.jpg';
    
    // Si ya es una URL completa (http o https)
    if (imageName.startsWith('http')) {
      return imageName;
    }
    
    // Si la imagen ya tiene la ruta completa, solo añade el slash al inicio
    if (imageName.includes('uploads/')) {
      return imageName.startsWith('/') ? imageName : `/${imageName}`;
    }
    
    // Si es solo el nombre del archivo (caso raro)
    return `/uploads/products/${imageName}`;
  };

  const { shipping, total } = useMemo(() => {
    const shippingCost = subtotal > 100000 ? 0 : 0;
    return {
      shipping: shippingCost,
      total: subtotal + shippingCost
    };
  }, [subtotal]);

  const handleInputChange = useCallback((name, value) => {
    setShippingInfo(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    let isValid = true;

    const requiredFields = ['name', 'email', 'address', 'city', 'state', 'phone', 'legalId'];
    requiredFields.forEach(field => {
      if (!shippingInfo[field]?.trim()) {
        errors[field] = 'Este campo es requerido';
        isValid = false;
      }
    });

    if (shippingInfo.email && !/^\S+@\S+\.\S+$/.test(shippingInfo.email)) {
      errors.email = 'Por favor ingresa un correo electrónico válido';
      isValid = false;
    }

    if (shippingInfo.phone) {
      const phoneDigits = shippingInfo.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.phone = 'El teléfono debe tener al menos 10 dígitos (sin código de país)';
        isValid = false;
      }
    }

    if (shippingInfo.legalId && !/^[0-9]{6,12}$/.test(shippingInfo.legalId)) {
      errors.legalId = 'Documento de identidad inválido. Debe tener entre 6 y 12 dígitos';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  }, [shippingInfo]);

  const createOrder = useCallback(async (orderData, token) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/orders`, 
        orderData, 
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );
      
      if (!response?.data?.success || !response?.data?.order?._id) {
        throw new Error('No se pudo crear la orden');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al crear orden:', error);
      throw new Error(error.response?.data?.error || 'Error al crear la orden');
    }
  }, [API_URL]);

  const redirectToWompiCheckout = useCallback(async (orderId, token) => {
    const response = await axios.post(
      `${API_URL}/api/orders/${orderId}/wompi-checkout`,
      {},
      { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
    );
    const checkout = response.data?.checkout;
    if (!response.data?.success || !checkout) {
      throw new Error(response.data?.error || 'No se pudo preparar el checkout de Wompi');
    }

    const baseUrl = 'https://checkout.wompi.co/p/';
    const params = new URLSearchParams({
      'public-key': checkout.publicKey,
      'currency': 'COP',
      'amount-in-cents': checkout.amountInCents.toString(),
      'reference': checkout.reference,
      'signature:integrity': checkout.signature,
      'redirect-url': checkout.redirectUrl,
      'acceptance-token': checkout.acceptanceToken
    });

    if (checkout.customerData?.email) {
      params.append('customer-data', JSON.stringify(checkout.customerData));
    }

    const wompiCheckoutUrl = `${baseUrl}?${params.toString()}`;
    localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({ orderId, createdAt: Date.now() }));
    window.location.href = wompiCheckoutUrl;
  }, []);

  const handleCheckout = useCallback(async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      setError('Tu carrito está vacío');
      return;
    }

    if (cleaningPendingCheckout) {
      setError('Estamos verificando tu intento de pago anterior. Espera un momento antes de continuar.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Debes iniciar sesión para continuar con la compra');
      navigate('/login', { state: { from: '/cart' } });
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    let createdOrderId = null;
    try {
      const orderData = {
        orderItems: cartItems.map(item => ({
          product: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image || null
        })),
        shippingAddress: {
          name: shippingInfo.name.trim(),
          email: shippingInfo.email.trim().toLowerCase(),
          address: shippingInfo.address.trim(),
          city: shippingInfo.city.trim(),
          state: shippingInfo.state.trim(),
          postalCode: shippingInfo.postalCode?.trim() || '000000',
          phone: shippingInfo.phone.replace(/\D/g, ''),
          legalId: shippingInfo.legalId,
          legalIdType: shippingInfo.legalIdType
        },
        paymentMethod: 'credit_card',
        itemsPrice: subtotal,
        shippingPrice: shipping,
        totalPrice: total
      };

      const orderResponse = await createOrder(orderData, token);
      const orderId = orderResponse.order._id;
      createdOrderId = orderId;

      await redirectToWompiCheckout(orderId, token);

    } catch (error) {
      console.error('Error en checkout:', error);
      // Si Wompi no abrió, no dejamos un pedido pendiente que se duplicaría al reintentar.
      if (createdOrderId) {
        try {
          await axios.post(`${API_URL}/api/orders/${createdOrderId}/cancel-unpaid`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000
          });
          localStorage.removeItem(PENDING_CHECKOUT_KEY);
        } catch (cancelError) {
          console.error('No se pudo limpiar el borrador de checkout:', cancelError);
        }
      }
      setError(error.message || 'Error al procesar el pedido. Verifica tus datos e intenta nuevamente.');
      setProcessing(false);
    }
  }, [
    cartItems,
    cleaningPendingCheckout,
    validateForm, 
    navigate, 
    shippingInfo, 
    subtotal, 
    shipping, 
    createOrder, 
    redirectToWompiCheckout,
    API_URL
  ]);

  const handleRemoveItem = useCallback((productId) => {
    removeFromCart(productId);
    setError('');
    setSuccess('');
  }, [removeFromCart]);

  const handleUpdateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
    setError('');
    setSuccess('');
  }, [removeFromCart, updateQuantity]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const handleDismissError = useCallback(() => {
    setError('');
  }, []);

  if (authLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FCFA] via-white to-[#EFF8F5] pt-28 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto lg:max-w-none">
          <div className="mb-8">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#0C4B45]">Compra segura</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">Tu carrito de compras</h1>
            <p className="text-gray-600 mt-2">Revisa tus productos y completa los datos para recibir tu pedido.</p>
          </div>
          
          {error && (
            <StatusMessage 
              type="error" 
              message={error} 
              onRetry={handleReload}
              onDismiss={handleDismissError}
            />
          )}

          {success && (
            <StatusMessage 
              type="success" 
              message={success} 
            />
          )}
          

          <div className="lg:grid lg:grid-cols-12 lg:gap-x-12 lg:items-start">
            <div className="lg:col-span-7">
              {cartItems.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-10 text-center border border-gray-100">
                  <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-2xl bg-[#EFF8F5] mb-4">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Tu carrito está vacío</h2>
                  <p className="text-gray-500 mt-1 mb-5">Encuentra productos para agregarlos a tu pedido.</p>
                  <button
                    onClick={() => navigate('/products')}
                    className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-[#0C4B45] hover:bg-[#083D38] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0C4B45] transition-colors"
                  >
                    Ver Productos
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Productos</h2>
                    <span className="rounded-full bg-[#EFF8F5] px-3 py-1 text-sm font-semibold text-[#0C4B45]">{cartItems.length} {cartItems.length === 1 ? 'artículo' : 'artículos'}</span>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {cartItems.map(item => (
                      <CartItem 
                        key={item._id} 
                        item={item} 
                        onUpdateQuantity={handleUpdateQuantity}
                        onRemoveItem={handleRemoveItem}
                        getImageUrl={getImageUrl}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-10 lg:mt-0 lg:col-span-5">
              <div className="bg-white rounded-2xl shadow-lg shadow-[#0C4B45]/5 p-6 border border-gray-100 lg:sticky lg:top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Resumen del pedido</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">${subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Envío</span>
                    <span className="font-medium">{shipping === 0 ? 'Gratis' : `$${shipping.toLocaleString()}`}</span>
                  </div>
                  <div className="flex justify-between pt-4 border-t border-gray-200">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-[#0C4B45]">${total.toLocaleString()}</span>
                  </div>
                </div>

                <form onSubmit={handleCheckout} className="space-y-6">
                  <ShippingForm 
                    shippingInfo={shippingInfo} 
                    onChange={handleInputChange}
                    errors={formErrors}
                    user={user}
                  />

                  <div className="pt-4 border-t border-gray-200">
                    <button
                    type="submit"
                    disabled={processing || cleaningPendingCheckout || cartItems.length === 0}
                    className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-colors flex items-center justify-center ${
                      processing || cleaningPendingCheckout || cartItems.length === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 shadow-sm hover:shadow-md'
                    }`}
                    >
                      {processing || cleaningPendingCheckout ? (
                        <>
                          <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                          {cleaningPendingCheckout ? 'Verificando intento anterior...' : 'Procesando...'}
                        </>
                      ) : (
                        <>
                          <CreditCardIcon className="h-5 w-5 mr-2" />
                          Pagar con Wompi
                        </>
                      )}
                    </button>
                    <div className="mt-4 flex items-center justify-center text-xs text-gray-500">
                      <ShieldCheckIcon className="h-4 w-4 mr-1 text-green-500" />
                      <span>Pago seguro con encriptación SSL</span>

                    </div>
                    
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <WppChat />
    </div>
  );
};

export default CartPage;
